import React, { useState } from 'react';
import './TopWidgetEdit.css';
import { useWidgetEdit } from '../hooks/useWidgetEdit';
import TextSettingsSection from '../WidgetEditComponents/TextSettingsSection';
import MoreSettingsSection from '../WidgetEditComponents/MoreSettingsSection';
import AdvancedSettingsSection from '../WidgetEditComponents/AdvancedSettingsSection';
import RevolutionaryLayoutSettings from '../WidgetEditComponents/RevolutionaryLayoutSettings';
import BragBarLogosManagement from '../WidgetEditComponents/BragBarLogosManagement';
import SaveTemplatePopup from '../WidgetEditComponents/SaveTemplatePopup';

function BragBarEdit({ widget, onUpdate, colors }) {
    const { editData, handleInputChange, uploadImage, handleSliderChange } = useWidgetEdit(widget, onUpdate);
    
    // ✅ SAVE TEMPLATE POPUP STATE
    const [showSaveTemplatePopup, setShowSaveTemplatePopup] = useState(false);
    
    // 🚀 REVOLUTIONARY TEXT SETTINGS - Mit Show/Hide Toggles
    const textConfig = [
        {
            fieldName: 'titleText',
            label: 'Brag Bar Title',
            type: 'input',
            placeholder: 'Trusted by industry leaders...',
            fontField: 'titleFont',
            fontType: 'small',
            effectField: 'titleEffect',
            showField: 'showTitle' // BragBar verwendet showTitle statt showTitleText
        }
    ];

    // Logo Management Functions
    const addLogo = () => {
        const currentLogos = editData.logos || [];
        const newLogo = {
            id: `logo${Date.now()}`,
            image: '',
            alt: `Brand Logo ${currentLogos.length + 1}`,
            link: '',
            width: '',
            height: ''
        };
        const updatedLogos = [...currentLogos, newLogo];
        handleInputChange({ target: { name: 'logos', value: updatedLogos } });
    };

    const removeLogo = (logoId) => {
        const currentLogos = editData.logos || [];
        const updatedLogos = currentLogos.filter(logo => logo.id !== logoId);
        handleInputChange({ target: { name: 'logos', value: updatedLogos } });
    };

    const updateLogo = (logoId, field, value) => {
        const currentLogos = editData.logos || [];
        const updatedLogos = currentLogos.map(logo => 
            logo.id === logoId ? { ...logo, [field]: value } : logo
        );
        handleInputChange({ target: { name: 'logos', value: updatedLogos } });
    };

    // 🎨 CLEAN COLOR CONFIGURATION
    const colorConfig = [
        [
            {
                fieldName: 'titleColor',
                label: 'Title',
                defaultValue: '#666666',
                icon: '/img/text-fat.svg'
            },
            {
                fieldName: 'backgroundColor',
                label: 'Background',
                defaultValue: '#ffffff',
                icon: '/img/background.svg'
            }
        ],
        [
            {
                fieldName: 'logoContainerBackground',
                label: 'Logo Container',
                defaultValue: 'transparent',
                icon: '/img/background.svg'
            },
            {
                fieldName: 'logoContainerBorderColor',
                label: 'Container Border',
                defaultValue: '#e0e0e0',
                icon: '/img/border.svg'
            }
        ]
    ];

    // 🔧 ADVANCED SETTINGS CONFIGURATION - Modern Regler Style
    // 🚀 CUSTOM LAYOUT PROPERTIES - Keine widget-spezifischen Layout Properties
    const customLayoutProperties = [];

    const advancedConfig = [
        // 📐 Widget-spezifische Dimensions
        {
            fieldName: 'logoHeight',
            label: 'Logo Height',
            type: 'slider',
            defaultValue: 40,
            min: 20,
            max: 80,
            step: 2,
            unit: 'px',
            color: '#10b981',
            icon: '/img/element.svg'
        },
        {
            fieldName: 'logoMaxWidth',
            label: 'Logo Max Width',
            type: 'slider',
            defaultValue: 160,
            min: 80,
            max: 300,
            step: 10,
            unit: 'px',
            color: '#3b82f6',
            icon: '/img/element.svg'
        },
        {
            fieldName: 'contentMaxWidth',
            label: 'Content Max Width',
            type: 'number',
            defaultValue: 1200,
            min: 600,
            max: 1400,
            step: 50,
            unit: 'px',
            icon: '/img/element.svg'
        },
        {
            fieldName: 'logoSpacing',
            label: 'Logo Spacing',
            type: 'slider',
            defaultValue: 40,
            min: 20,
            max: 80,
            step: 5,
            unit: 'px',
            color: '#f59e0b',
            icon: '/img/element.svg'
        },
        
        // 🎨 Display
        {
            fieldName: 'logoOpacity',
            label: 'Logo Opacity',
            type: 'slider',
            defaultValue: 1.0,
            min: 0.3,
            max: 1.0,
            step: 0.1,
            unit: '',
            color: '#ef4444',
            icon: '/img/background.svg'
        },
        {
            fieldName: 'logoHoverOpacity',
            label: 'Hover Opacity',
            type: 'slider',
            defaultValue: 1.0,
            min: 0.5,
            max: 1.0,
            step: 0.1,
            unit: '',
            color: '#06b6d4',
            icon: '/img/background.svg'
        },
        
        // ⚙️ Behavior
        {
            fieldName: 'enableHoverEffects',
            label: 'Enable Hover Effects',
            type: 'toggle',
            defaultValue: true,
            icon: '/img/background.svg'
        },
        {
            fieldName: 'enableAnimations',
            label: 'Enable Animations',
            type: 'toggle',
            defaultValue: false,
            icon: '/img/background.svg'
        },
        {
            fieldName: 'enableLogoLinks',
            label: 'Enable Logo Links',
            type: 'toggle',
            defaultValue: false,
            icon: '/img/background.svg'
        }
    ];

    // 📝 FONT CONFIGURATION
    const fontConfig = [
        {
            label: 'Title Font',
            sizeField: 'titleSize',
            weightField: 'titleWeight',
            defaultSize: '16',
            defaultWeight: '600'
        }
    ];

    return (
        <div className="top-widget-edit-container">
            
            {/* 🚀 REVOLUTIONARY TEXT SETTINGS SECTION */}
            <TextSettingsSection 
                        textConfig={textConfig}
                        editData={editData}
                        handleInputChange={handleInputChange}
                        showFontSelector={true}
                        showTextEffects={true}
                showButtonEffects={false}
            />

            {/* 🏢 LOGOS MANAGEMENT SECTION */}
            <BragBarLogosManagement 
                logos={editData.logos || []}
                addLogo={addLogo}
                removeLogo={removeLogo}
                updateLogo={updateLogo}
                uploadImage={uploadImage}
                editData={editData}
                handleInputChange={handleInputChange}
            />

            {/* 🚀 REVOLUTIONARY LAYOUT SETTINGS - Standard Layout Properties */}
            <RevolutionaryLayoutSettings 
                editData={editData}
                handleInputChange={handleInputChange}
                customProperties={customLayoutProperties}
            />

            {/* 🔧 ADVANCED SETTINGS SECTION - Widget-spezifische Properties */}
            <AdvancedSettingsSection 
                advancedConfig={advancedConfig}
                editData={editData}
                handleInputChange={handleInputChange}
                handleSliderChange={handleSliderChange}
                defaultExpanded={false}
            />

            {/* 🎛️ MORE SETTINGS SECTION - Ohne Layout Config */}
            <MoreSettingsSection 
                colors={colors}
                colorConfig={colorConfig}
                fontConfig={fontConfig}
                layoutConfig={[]} // Entfernt - jetzt in Advanced Settings
                editData={editData}
                handleInputChange={handleInputChange}
            />

            {/* 💾 SAVE AS TEMPLATE BUTTON */}
            <div className="save-template-section">
                <button 
                    className="save-template-btn"
                    onClick={() => setShowSaveTemplatePopup(true)}
                    title="Save this widget configuration as a reusable template"
                >
                    <i className="fas fa-save"></i>
                    Save as Template
                </button>
            </div>

            {/* 💾 SAVE TEMPLATE POPUP */}
            <SaveTemplatePopup
                isOpen={showSaveTemplatePopup}
                onClose={() => setShowSaveTemplatePopup(false)}
                widgetData={editData}
                originalWidgetType={widget.type}
            />
            
        </div>
    );
}

export default BragBarEdit;